import { Column, matchString, Table, TableOptions } from '@acceso/design-system';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { RenderActionButtons } from '@/components/RenderActionButtons';
import { TableRow } from '@/components/TableRow';
import { useBroadcastScheduleProgram } from '@/features/broadcast-schedule-program/context/BroadcastScheduleProgramContext';
import { TClipOcr } from '@/features/clips/types';
import { getOffsetHHMMSS } from '@/utils/hh-mm-ss-into-number';

type TBroadcastScheduleTableProps = {
  items: TClipOcr[];
  onPreview?: (item: TClipOcr) => void;
  onUpdate?: (item: TClipOcr) => void;
  onDelete?: (item: TClipOcr) => void;
};

export function ClipRotuloTable({
  items,
  onPreview,
  onUpdate,
  onDelete,
}: TBroadcastScheduleTableProps) {
  const { activeClip } = useBroadcastScheduleProgram();
  const { t } = useTranslation();

  const options: Partial<TableOptions> = {
    initialSortBy: { id: 'updated_at', order: 'desc' },
    filters: [
      {
        name: 'edt',
        placeholder: t('Asignado a EDT'),
        options: [],
        onChange: () => {
          return items;
        },
      },
    ],
    searchBarPlaceholder: t('Buscar por ŕotulo'),
  };

  const columns: Column<TClipOcr>[] = useMemo(
    () => [
      {
        id: 'ocr',
        title: t('Rótulo'),
        searchable: true,
        render: item => {
          return <TableRow content={item.text || ''} className="!font-semibold" />;
        },
        matchFn: (query, item) => {
          return matchString(item?.text || '', query);
        },
      },
      {
        id: 'timeline',
        title: t('Timeline'),
        render: item => {
          if (!activeClip) return <></>;

          const seconds = getOffsetHHMMSS(item.start_datetime, activeClip.start_datetime);

          return <TableRow content={seconds} />;
        },
      },
      {
        id: 'actions',
        title: '',
        className: 'items-end',
        width: '5%',
        render: item => (
          <div className="inline-flex w-full justify-end">
            <RenderActionButtons
              item={item}
              onPreview={() => onPreview?.(item)}
              onUpdate={() => onUpdate?.(item)}
              onDelete={() => onDelete?.(item)}
            />
          </div>
        ),
      },
    ],
    [t, activeClip?.start_datetime]
  );

  return <Table options={options} columns={columns} data={items} ignoreWrap />;
}
